import { Breakpoint } from '@mui/material/styles';

import { breakpoints as themeBreakpoints } from './breakpoints';
import { FONT_SIZE_HTML, FONT_SIZE_BODY } from './consts';

export const roundCssValue = (value: number, decimals = 4) =>
  Math.round(value * 10 ** decimals) / 10 ** decimals;

export const pxToRem = (px: number | string): string =>
  `${roundCssValue(
    ((typeof px === 'number' ? px : parseFloat(px)) / FONT_SIZE_HTML) * (FONT_SIZE_BODY / 14),
  )}rem`;

export const remToPx = (rem: number | string): number =>
  (typeof rem === 'number' ? rem : parseFloat(rem)) * FONT_SIZE_HTML;

export const lineHeightPxToUnitless = (lineHeightPx: number, fontSizePx: number) =>
  roundCssValue(lineHeightPx / fontSizePx);

/**
 * Generate a responsive version of a given font size value
 *
 * @param fontSize Font size in rem or pixels. Pass rem values as strings and pixel values as numbers.
 * @param factor The factor to scale the font size down by.
 * The smallest breakpoint will have a font size of `fontSize * factor`, while the largest breakpoint will have a font size of `fontSize`.
 * Default is `0.75`.
 * @param breakpoints The breakpoints to apply the font size to. Default is `['md', 'lg']`.
 *
 * @example
 * responsiveFontSize('3rem', 0.75)
 * // Returns:
 * {
 *   "fontSize": "3rem", // Fallback (max)
 *   "@media (min-width:900px)": { // md (min)
 *     "fontSize": "2.25rem"
 *   },
 *   "@media (min-width:1200px)": { // lg
 *     "fontSize": "2.5rem"
 *   },
 *   "@media (min-width:1536px)": { // xl
 *     "fontSize": "2.75rem"
 *   },
 *   "@media (min-width:2060px)": { // xxl (max)
 *     "fontSize": "3rem"
 *   }
 * }
 */
export const responsiveFontSize = (
  fontSize: string | number,
  factor = 0.7,
  breakpoints: Breakpoint[] = ['md', 'lg'],
) => {
  const max = parseFloat(typeof fontSize === 'number' ? pxToRem(fontSize) : fontSize);
  const min = max * factor;
  const unit = 'rem';

  const breakpointValues = breakpoints
    .map((key) => (themeBreakpoints.values as Record<string, number>)[key] ?? null)
    .filter((value) => value !== null);

  const totalBreakpoints = breakpointValues.length;

  const mediaQueries = breakpointValues.reduce((acc, breakpointValue, index) => {
    const fontSizeValue = min + ((max - min) * index) / (totalBreakpoints - 1);
    return {
      ...acc,
      [`@media (min-width:${breakpointValue}px)`]: {
        fontSize: `${roundCssValue(fontSizeValue)}${unit}`,
      },
    };
  }, {} as Record<string, { fontSize: string }>);

  return {
    fontSize: `${max}${unit}`,
    ...mediaQueries,
  };
};
